import { Link } from "react-router-dom";
import { useProcessStats } from "@/api/queries/processes";
import { ApiError } from "@/api/client";
import { SectionCard } from "@/components/SectionCard";
import { CaseStatusBadge } from "@/components/ProcessBits";
import { formatDauer, formatQuote, stichprobenHinweis } from "./statsFormat";
import type { CaseStatusValue } from "@/types";

// #303 (UC-2): „läuft mein Prozess?" — das Aggregat über ALLE Einrichtungen.
//
// ⚠ Ausschliesslich Zahlen. Der Vorlagen-Verantwortliche ist NICHT automatisch Inhaber der
// Läufe: die Einrichtungen gehören denen, die sie angelegt haben, und deren Vorgangsdaten
// sind seine Sache nicht. Er darf wissen, WIE OFT und WIE GUT sein Prozess läuft — nicht,
// WORÜBER. Der Master liefert deshalb keine Feldinhalte und keine Lauf-Ids.

// Nach Dringlichkeit, nicht alphabetisch — dieselbe Reihung wie in der Lauf-Liste.
const REIHENFOLGE: CaseStatusValue[] = [
  "blocked",
  "waiting_approval",
  "waiting_input",
  "running",
  "paused",
  "done",
  "aborted",
];

function Kennzahl({ label, wert, ton }: { label: string; wert: string; ton?: "warn" | "alert" }) {
  return (
    <div className="min-w-[110px]">
      <div className="text-[11px] uppercase tracking-wide text-text3">{label}</div>
      <div
        className={`text-[18px] font-bold tracking-tight ${
          ton === "alert" ? "text-danger" : ton === "warn" ? "text-warn" : "text-text"
        }`}
      >
        {wert}
      </div>
    </div>
  );
}

export function ProcessStatsCard({ processId }: { processId: string }) {
  const q = useProcessStats(processId);

  // Der Master antwortet einem Nicht-Berechtigten mit 404 (byte-gleich zu „gibt es nicht" —
  // kein Existenz-Orakel über fremde Arbeit). Daraus darf hier KEIN „keine Läufe" werden:
  // die Karte verschwindet, statt eine Zahl zu behaupten, die sie nicht kennt.
  const status = q.error instanceof ApiError ? q.error.status : 0;
  if (q.isError && status === 404) return null;
  // #303 (Review L5): jeder ANDERE Fehler (500, Netz, 503 ohne Lauf-Repo) wird BENANNT —
  // Haus-Regel „ein Ladefehler ist kein leerer Zustand". Vorher verschwand die Karte auch
  // dann lautlos, und ein Ausfall der Kennzahlen sah aus wie fehlende Berechtigung.
  if (q.isError) {
    return (
      <SectionCard title="Läufe dieser Vorlage" testId="process-stats-fehler">
        <p className="text-[12.5px] text-danger">
          Die Kennzahlen sind gerade nicht abrufbar — das heisst nicht, dass es keine Läufe gibt.
        </p>
      </SectionCard>
    );
  }
  if (!q.data) return null;

  const s = q.data;
  const hinweis = stichprobenHinweis(s);
  // Fremdformat-Härtung: ein fehlendes Feld darf die Karte nicht fällen (Regel aus
  // `darfVorgang` — eine Anzeige-Hilfe reisst nie die Seite ab).
  const proStatus: Record<string, number> =
    s.proStatus && typeof s.proStatus === "object" ? s.proStatus : {};
  const eintraege = REIHENFOLGE.filter((k) => (proStatus[k] ?? 0) > 0);
  // Ein künftiger Zustand, den diese Oberfläche noch nicht kennt, fällt sonst still weg.
  const unbekannt = Object.keys(proStatus).filter(
    (k) => !REIHENFOLGE.includes(k as CaseStatusValue) && (proStatus[k] ?? 0) > 0,
  );

  return (
    <SectionCard
      title="Läufe dieser Vorlage"
      description="Nur Kennzahlen — Vorgangsdaten der Einrichtungen bleiben bei deren Inhabern."
      testId="process-stats"
      action={
        s.laeufe > 0 ? (
          <Link
            to={`/cases?templateId=${encodeURIComponent(processId)}`}
            className="text-[12.5px] text-accent hover:underline"
          >
            Läufe ansehen →
          </Link>
        ) : undefined
      }
    >
      <div className="flex flex-wrap gap-x-8 gap-y-3">
        <Kennzahl label="Läufe" wert={String(s.laeufe)} />
        <Kennzahl label="Einrichtungen" wert={String(s.instanzen)} />
        <Kennzahl
          label="Durchlaufzeit ⌀"
          wert={s.durchlaufzeitSek ? formatDauer(s.durchlaufzeitSek.avg) : "—"}
        />
        <Kennzahl
          label="Durchlaufzeit Median"
          wert={s.durchlaufzeitSek ? formatDauer(s.durchlaufzeitSek.median) : "—"}
        />
        <Kennzahl
          label="Fehlerquote"
          wert={formatQuote(s.fehlerquote)}
          ton={s.fehlerquote >= 0.25 ? "alert" : s.fehlerquote >= 0.1 ? "warn" : undefined}
        />
      </div>

      {s.durchlaufzeitSek && (
        <p className="mt-1 text-[11.5px] text-text3">
          Durchlaufzeit über {s.durchlaufzeitSek.n} abgeschlossene{" "}
          {s.durchlaufzeitSek.n === 1 ? "Lauf" : "Läufe"}. Fehlerquote = abgebrochen + blockiert.
        </p>
      )}

      {eintraege.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="process-stats-status">
          {eintraege.map((k) => (
            <span key={k} className="inline-flex items-center gap-1.5">
              <CaseStatusBadge status={k} />
              <span className="text-[12.5px] font-semibold text-text2">{proStatus[k]}</span>
            </span>
          ))}
          {unbekannt.map((k) => (
            <span key={k} className="text-[12px] text-text3">
              {k}: {proStatus[k]}
            </span>
          ))}
        </div>
      )}

      {hinweis && (
        <p className="mt-3 text-[12px] text-warn" data-testid="process-stats-gekuerzt">
          {hinweis}
        </p>
      )}
    </SectionCard>
  );
}
